// frontend/src/pages/Confidentialite.js — Politique de Confidentialité
import React from "react";
import { useNavigate } from "react-router-dom";
import LegalPage, { Section, P, B, UL, LI, Table } from "../components/LegalPage";

const Confidentialite = () => {
  const navigate = useNavigate();
  return (
    <LegalPage
      title="Politique de Confidentialité"
      subtitle="La présente politique explique quelles données personnelles 1PARC collecte, pourquoi, avec qui elles sont partagées, et quels sont vos droits."
      lastUpdated="14 juin 2026"
    >
      <Section n={1} title="Responsable du traitement">
        <P>Le responsable du traitement des données personnelles est <B>1 PARC SARL</B> (RCCM 2014-M1 du 08-09-2023), dont le siège est situé au 2 Plateaux Paillet, Abidjan, Côte d'Ivoire.</P>
        <P>Nous traitons vos données conformément à la <B>Loi n°2013-450 du 19 juin 2013 relative à la protection des données à caractère personnel</B> en Côte d'Ivoire et sous le contrôle de l'Autorité de Régulation des Télécommunications/TIC de Côte d'Ivoire (<B>ARTCI</B>).</P>
        <P>Contact protection des données : <B>contact@1parc.com</B>.</P>
      </Section>

      <Section n={2} title="Données que nous collectons">
        <P>Nous collectons uniquement les données nécessaires au fonctionnement du service :</P>
        <UL>
          <LI><B>Données de compte</B> : nom, adresse e-mail, numéro de téléphone, mot de passe (stocké de manière chiffrée), rôle ; pour les professionnels : raison sociale et numéro fiscal.</LI>
          <LI><B>Données de véhicule</B> : plaque d'immatriculation, numéro de châssis (VIN), marque, modèle, année, type de carburant, kilométrage, informations d'assurance et de contrôle technique, nom figurant sur la carte grise.</LI>
          <LI><B>Données d'activité</B> : demandes de service (y compris la <B>localisation GPS</B> lorsque vous la fournissez), devis, factures, historique d'entretien, paiements et écritures de caisse.</LI>
          {/* The app declares CAMERA and uses image_picker to photograph a
              vehicle at reception. Google Play compares the Data safety form
              against this page, and declaring photo collection on a policy that
              never mentions photos is a mismatch. */}
          <LI><B>Photographies</B> : images de votre véhicule prises à la réception ou lors d'un constat, ainsi que les photos que vous ajoutez vous-même depuis l'application. Elles sont rattachées au véhicule concerné et visibles par l'atelier qui en a la charge.</LI>
          <LI><B>Données techniques</B> : journaux de connexion, adresse IP, type d'appareil et de navigateur, nécessaires à la sécurité et au bon fonctionnement.</LI>
        </UL>
        {/* local_auth. The fingerprint never leaves the handset, so the Data
            safety form declares nothing collected — saying so explicitly costs
            one sentence and pre-empts the question. */}
        <P>
          <B>Données biométriques</B> : si vous activez le déverrouillage par empreinte
          digitale ou reconnaissance faciale dans l'application mobile, la vérification est
          effectuée par votre téléphone. Aucune donnée biométrique n'est transmise à 1PARC
          ni enregistrée sur nos serveurs : nous recevons uniquement le résultat
          (réussite ou échec) de la vérification.
        </P>
      </Section>

      <Section n={3} title="Finalités d'utilisation">
        <P>Vos données sont utilisées pour :</P>
        <UL>
          <LI>créer et gérer votre compte et authentifier vos accès ;</LI>
          <LI>fournir les services (mise en relation, devis, factures, paiements, historique) ;</LI>
          {/* Every category declared in §02 needs a purpose here, or the Data
              safety form has a collection with no stated use. */}
          <LI>documenter l'état d'un véhicule à la réception et à la restitution, au moyen de photographies, afin de protéger le client comme l'atelier en cas de contestation ;</LI>
          <LI>vous envoyer des notifications par SMS et e-mail liées à votre activité ;</LI>
          <LI>vous adresser des rappels (assurance, contrôle technique, vidange) ;</LI>
          <LI>améliorer la Plateforme et fournir des fonctions d'aide à la décision ;</LI>
          <LI>assurer la sécurité, prévenir la fraude et respecter nos obligations légales.</LI>
        </UL>
      </Section>

      <Section n={4} title="Partage avec des tiers (sous-traitants)">
        <P>Nous ne vendons pas vos données. Nous faisons appel à des prestataires techniques qui les traitent uniquement pour notre compte et selon nos instructions :</P>
        <Table
          head={["Prestataire", "Finalité"]}
          rows={[
            ["Wave", "Traitement des paiements mobiles"],
            ["Infobip / Orange", "Envoi des SMS"],
            ["Resend", "Envoi des e-mails transactionnels"],
            ["Anthropic", "Fonctions de diagnostic et de suggestion par IA"],
            ["Firebase (Google)", "Notifications et services applicatifs"],
            ["MongoDB Atlas", "Hébergement de la base de données"],
            ["Render", "Hébergement de l'application"],
          ]}
        />
        <P>Vos données peuvent également être partagées avec l'atelier ou le professionnel auquel vous êtes rattaché, dans la stricte mesure nécessaire à la prestation (par exemple, un atelier voit les véhicules et l'historique des clients qui lui sont liés).</P>
      </Section>

      <Section n={5} title="SMS et communications">
        <P>Nous vous envoyons des SMS et e-mails uniquement en lien avec votre activité : envoi d'un devis ou d'une facture, réception d'un véhicule, ajout d'un véhicule à votre compte, codes de vérification, demandes de liaison de véhicule, et rappels d'échéances (assurance, contrôle technique, vidange).</P>
        <P>Pour ne plus recevoir certaines communications, contactez-nous à <B>contact@1parc.com</B>. Les messages strictement nécessaires (sécurité, vérification) peuvent rester indispensables à l'utilisation du service.</P>
      </Section>

      <Section n={6} title="Cookies et traceurs">
        <P>Pour vous maintenir connecté, nous utilisons un jeton d'authentification stocké localement dans votre navigateur. Nous utilisons également le <B>Pixel Meta (Facebook)</B> à des fins de mesure et de marketing sur certaines pages. Vous pouvez gérer ces traceurs via les paramètres de votre navigateur et de votre compte publicitaire.</P>
      </Section>

      <Section n={7} title="Comptes créés par les ateliers">
        <P>Un atelier partenaire peut enregistrer votre véhicule et créer un compte préliminaire (« compte en attente d'activation ») à partir des informations que vous lui communiquez. Vous recevez alors une invitation à activer votre compte. Vous pouvez à tout moment demander l'accès, la rectification ou la suppression de ces données.</P>
      </Section>

      <Section n={8} title="Conservation des données">
        <P>Nous conservons vos données pendant la durée nécessaire aux finalités décrites, et aussi longtemps que votre compte est actif. L'historique d'entretien d'un véhicule peut être conservé pour préserver sa valeur et sa traçabilité. Au-delà, les données sont supprimées ou anonymisées, sous réserve des obligations légales (notamment comptables et fiscales).</P>
      </Section>

      <Section n={9} title="Sécurité">
        <P>Nous mettons en œuvre des mesures techniques et organisationnelles raisonnables pour protéger vos données : chiffrement des mots de passe, accès restreints, communications sécurisées. Aucun système n'étant infaillible, nous ne pouvons garantir une sécurité absolue.</P>
      </Section>

      <Section n={10} title="Vos droits">
        <P>Conformément à la loi applicable, vous disposez des droits d'<B>accès</B>, de <B>rectification</B>, de <B>suppression</B>, d'<B>opposition</B> et de <B>limitation</B> du traitement de vos données. Pour les exercer, écrivez à <B>contact@1parc.com</B> en justifiant de votre identité.</P>
        {/* Google Play checks that the deletion URL given in the Data safety
            form is reachable and consistent with this policy; linking it here
            is also how a user finds it. */}
        <P>Pour supprimer votre compte et les données associées, consultez la page dédiée : <button className="lk" onClick={() => navigate("/suppression-compte")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}>Suppression de compte</button>. Elle détaille ce qui est effacé et ce que la loi nous impose de conserver.</P>
      </Section>

      <Section n={11} title="Transferts hors de Côte d'Ivoire">
        <P>Certains de nos prestataires (hébergement, e-mail, IA) sont situés hors de Côte d'Ivoire. Lorsque vos données sont transférées à l'étranger, nous veillons à ce que des garanties appropriées encadrent ce transfert.</P>
      </Section>

      <Section n={12} title="Mineurs">
        <P>La Plateforme s'adresse à des personnes majeures. Nous ne collectons pas sciemment de données concernant des mineurs.</P>
      </Section>

      <Section n={13} title="Modifications">
        <P>Nous pouvons mettre à jour la présente politique. La date de dernière mise à jour figure en tête de page. Les modifications importantes vous seront notifiées par les moyens appropriés.</P>
      </Section>

      <Section n={14} title="Contact et réclamation">
        <P>Pour toute question ou réclamation relative à vos données : <B>contact@1parc.com</B> — 0799290000 — 2 Plateaux Paillet, Abidjan. Vous pouvez également saisir l'<B>ARTCI</B>, autorité de protection des données en Côte d'Ivoire.</P>
        <P>Voir aussi nos <button className="lk" onClick={() => navigate("/conditions")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}>Conditions Générales d'Utilisation</button>.</P>
      </Section>
    </LegalPage>
  );
};

export default Confidentialite;
